import {
  forwardRef, memo, useState, useCallback, useMemo, useEffect,
  type ForwardedRef, type MouseEventHandler, type ReactNode
} from 'react';
import { type TruncateOptions } from 'lodash';
import round from 'lodash/round';
import truncate from 'lodash/truncate';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import type { TypographyVariant } from '@mui/material';
import Box, { type BoxProps } from '@mui/material/Box';
// local imports
import { fontWeightBold, fontWeightMedium, fontWeightRegular } from '../styles/themeOptions';
import { MAX_DISPLAYED_TITLE_LENGTH } from '../config/params';
import { GetComponentProps } from '../helpers/types';
import { FormatMessageFuncValues, Values } from '../helpers/intl';
import BoxTypography from '../mixins/BoxTypography';
import StandardLink from '../elements/StandardLink';
import SimpleTooltip from '../elements/SimpleTooltip';
// SCSS imports
import {
  row, subtitle1Container, subtitle1Row, subtitle2Container, subtitle2Row,
  h5Container, h5Row, h6Container, h6Row, body1Container, body1Row, disabledText
} from './TruncatedTextLink.module.scss';

const TEXT_TRUNCATE_OPTIONS: TruncateOptions = {
  length: 3 * MAX_DISPLAYED_TITLE_LENGTH,
  separator: /\s+/u,
  omission: '…'
} as const;

export function getTruncatedText(title: string, length?: number): string {
  return truncate(title, length ? { ...TEXT_TRUNCATE_OPTIONS, length } : TEXT_TRUNCATE_OPTIONS);
}

export type TruncatedTextVariant = Exclude<TypographyVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'caption' | 'button' | 'overline'>;

const titleContainer: Record<TruncatedTextVariant, string> = {
  subtitle1: subtitle1Container,
  subtitle2: subtitle2Container,
  h5: h5Container,
  h6: h6Container,
  body1: body1Container,
  body2: h6Container
} as const;

const titleRow: Record<TruncatedTextVariant, string> = {
  subtitle1: subtitle1Row,
  subtitle2: subtitle2Row,
  h5: h5Row,
  h6: h6Row,
  body1: body1Row,
  body2: h6Row
} as const;

type TruncatedTextLinkProps = {
  readonly text?: string | null; // link text translated with React Intl
  readonly values?: Values; // values for link text translation with React Intl
  readonly suffix?: ReactNode;
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
  readonly length?: number; // length of truncated text
  readonly plain?: boolean;
  readonly bold?: boolean;
  readonly dark?: boolean;
  readonly active?: boolean;
  readonly inherit?: boolean;
  readonly variant?: TruncatedTextVariant | 'h1' | 'h2' | 'h3' | 'h4' | 'inherit';
  readonly maxLines?: number;
  readonly color?: string;
  readonly regular?: boolean;
  readonly to?: string; // React Router link
  readonly toState?: object; // React Router link state
  readonly href?: string | null; // static href
  readonly withoutTarget?: boolean; // do not add `target="_blank"` for static href
  readonly link?: string | null; // dynamic href translated with React Intl
  readonly onClick?: MouseEventHandler<HTMLAnchorElement> & MouseEventHandler<HTMLSpanElement>; // click handler function
  readonly disabled?: boolean;
  readonly withoutTooltip?: boolean;
  readonly className?: string;
  readonly sx?: GetComponentProps<typeof BoxTypography>['sx'] & GetComponentProps<typeof StandardLink>['sx'] & BoxProps['sx'];
};

const TruncatedTextLink = forwardRef<HTMLElement | HTMLAnchorElement, TruncatedTextLinkProps>(({
  text,
  values,
  suffix,
  startIcon,
  endIcon,
  length,
  plain,
  bold,
  dark,
  active,
  inherit,
  variant,
  maxLines,
  color,
  regular,
  disabled,
  withoutTooltip,
  to,
  toState,
  href,
  withoutTarget,
  link,
  onClick,
  className,
  ...props
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const handleTooltipOpen = useCallback(() => setOpen(true), []);
  const handleTooltipClose = useCallback(() => setOpen(false), []);

  const [translatedText, truncatedText, needsTootip] = useMemo(() => {
    const txt = text
      ? formatMessage({ id: text, defaultMessage: text }, values as FormatMessageFuncValues) as unknown as string
      : '';
    const truncated = text
      ? getTruncatedText(txt, length || (maxLines ? round(maxLines * MAX_DISPLAYED_TITLE_LENGTH) : undefined))
      : txt;
    return [
      suffix ? (
        <>
          {txt}
          {suffix}
        </>
      ) : txt,
      truncated,
      !withoutTooltip && !disabled && txt !== truncated
    ];
  }, [text, values, suffix, disabled, length, maxLines, withoutTooltip, formatMessage]);

  useEffect(() => {
    if (!needsTootip) setOpen(false);
  }, [needsTootip]);

  const naked = Boolean(maxLines) ||
    variant === 'inherit' || variant === 'h1' || variant === 'h2' || variant === 'h3' || variant === 'h4';

  const textLink = plain ? (
    <BoxTypography
        ref={naked ? ref : undefined}
        variant={variant || 'subtitle2'}
        color={color || (dark ? 'secondary.text' : 'secondary.main')}
        fontWeight={(bold && fontWeightBold) || (regular && fontWeightRegular) || fontWeightMedium}
        className={clsx(
          naked ? row : titleRow[variant || 'subtitle2'],
          disabled ? disabledText : undefined,
          className
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...naked ? props : {}}
    >
      {startIcon}
      {truncatedText}
      {suffix}
      {endIcon}
    </BoxTypography>
  ) : (
    <StandardLink
        ref={naked ? ref as ForwardedRef<HTMLAnchorElement> : undefined}
        variant={variant}
        bold={bold}
        dark={dark}
        active={active}
        inherit={inherit}
        to={disabled ? undefined : to}
        toState={disabled ? undefined : toState}
        href={disabled ? undefined : href}
        withoutTarget={withoutTarget}
        link={disabled ? undefined : link}
        onClick={disabled ? undefined : onClick}
        className={clsx(
          naked ? row : titleRow[variant || 'subtitle2'],
          className
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...naked ? props : {}}
    >
      {startIcon}
      {truncatedText}
      {suffix}
      {endIcon}
    </StandardLink>
  );

  const content = naked ? textLink : (
    <Box
        ref={ref}
        display="flex"
        alignItems="center"
        className={titleContainer[variant || 'subtitle2']}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
    >
      {textLink}
    </Box>
  );

  return needsTootip ? (
    <SimpleTooltip
        open={open}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
        disableInteractive
        disableFocusListener
        placement="top"
        title={translatedText}
    >
      {content}
    </SimpleTooltip>
  ) : content;
});

TruncatedTextLink.displayName = 'TruncatedTextLink';

export default memo(TruncatedTextLink);
